import { getDefaultStore } from 'jotai'
import { TMutedList } from '@/types'
import { currentPubkeyAtom, signerAtom } from './session'
import { fetchMuteList, publishOpaquely } from '@/services/client.service'
import * as kinds from '@nostr/tools/kinds'
import { now } from '@/lib/utils'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/error'
import i18n from 'i18next'
import { invalidateMuteList } from './invalidation'

const store = getDefaultStore()
const t = i18n.t.bind(i18n)

async function publishNewMuteListEvent(list: TMutedList): Promise<void> {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return
  const signer = await store.get(signerAtom)
  if (!signer) return

  console.debug('[mute] publishing:', {
    public: list.public.length,
    private: list.private.length
  })

  const tags = list.public.map((pubkey) => ['p', pubkey])
  const content = signer.nip04Encrypt
    ? await signer.nip04Encrypt(pubkey, JSON.stringify(list.private.map((pubkey) => ['p', pubkey])))
    : ''

  const event = await publishOpaquely({
    kind: kinds.Mutelist,
    content: content ?? '',
    created_at: now(),
    tags
  })
  if (!event) {
    console.warn('failed to publish updated mute list')
    toast.error(t('Failed to publish updated mute list.'))
    return
  }

  console.debug('[mute] published event:', event.id, 'created_at:', event.created_at)
  toast.success(t('Successfully updated mute list'))
  invalidateMuteList()
}

function checkMuteList(list: TMutedList) {
  if (list.public.length === 0 && list.private.length === 0) {
    const result = confirm(
      t(
        'Mute list not found. Do you want to create a new one? If you have muted users before, please DO NOT confirm as this operation will cause you to lose your previous mute list.'
      )
    )
    if (!result) {
      throw new Error('Mute list not found')
    }
  }
}

export async function mutePublicly(target: string): Promise<void> {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return

  try {
    const { list: fresh } = await fetchMuteList(pubkey, true)
    checkMuteList(fresh)

    if (!fresh.public.includes(target)) {
      fresh.public.push(target)
      const idx = fresh.private.indexOf(target)
      if (idx !== -1) fresh.private.splice(idx, 1)
      await publishNewMuteListEvent(fresh)
    }
  } catch (error) {
    console.error('[mute] mutePublicly failed:', error)
    toast.error(t('Failed to mute user publicly') + ': ' + getErrorMessage(error))
  }
}

export async function mutePrivately(target: string): Promise<void> {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return

  try {
    const { list: fresh } = await fetchMuteList(pubkey, true)
    checkMuteList(fresh)

    if (!fresh.private.includes(target)) {
      fresh.private.push(target)
      const idx = fresh.public.indexOf(target)
      if (idx !== -1) fresh.public.splice(idx, 1)
      await publishNewMuteListEvent(fresh)
    }
  } catch (error) {
    console.error('[mute] mutePrivately failed:', error)
    toast.error(t('Failed to mute user privately') + ': ' + getErrorMessage(error))
  }
}

export async function unmute(target: string): Promise<void> {
  const pubkey = store.get(currentPubkeyAtom)
  if (!pubkey) return

  try {
    const { list: fresh } = await fetchMuteList(pubkey, true)
    checkMuteList(fresh)

    let modified = false
    const privateIdx = fresh.private.indexOf(target)
    if (privateIdx !== -1) {
      fresh.private.splice(privateIdx, 1)
      modified = true
    }
    const publicIdx = fresh.public.indexOf(target)
    if (publicIdx !== -1) {
      fresh.public.splice(publicIdx, 1)
      modified = true
    }

    if (modified) {
      await publishNewMuteListEvent(fresh)
    }
  } catch (error) {
    console.error('[mute] unmute failed:', error)
    toast.error(t('Failed to unmute user') + ': ' + getErrorMessage(error))
  }
}
